import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Plus, Building2, Trash2 } from "lucide-react";

export default function Areas() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const { data: departments = [], isLoading } = useQuery({ queryKey: ["departments"], queryFn: () => api.get("/departments").then((r) => r.data) });

  const create = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      await api.post("/departments", form);
      toast.success("Área creada");
      qc.invalidateQueries({ queryKey: ["departments"] });
      setForm({ name: "", description: "" });
      setOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta área? Solo es posible si no tiene funcionarios ni tareas asignadas.")) return;
    try {
      await api.delete(`/departments/${id}`);
      qc.invalidateQueries({ queryKey: ["departments"] });
      toast.success("Área eliminada");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="areas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Áreas</h1>
          <p className="text-sm text-slate-500">Departamentos de tu empresa</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-slate-900 font-bold hover:bg-slate-800" data-testid="add-area-btn">
              <Plus className="mr-1 h-4 w-4" /> Nueva
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="area-dialog">
            <DialogHeader><DialogTitle className="font-heading">Nueva área</DialogTitle><DialogDescription>Agrega un departamento a tu empresa.</DialogDescription></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="area-name" /></div>
              <div><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" rows={2} /></div>
              <Button onClick={create} disabled={saving} className="w-full rounded-full bg-slate-900 font-bold" data-testid="save-area-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear área"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {departments.map((d) => (
            <div key={d.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`area-${d.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100"><Building2 className="h-5 w-5 text-slate-600" /></div>
                <button onClick={() => remove(d.id)} className="text-slate-300 hover:text-red-500" data-testid={`delete-area-${d.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-3 font-heading font-bold text-slate-900">{d.name}</div>
              {d.description && <div className="text-xs text-slate-400">{d.description}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
